<template>
  <div class="container-fluid">
    <div class="card">
      <div class="card-body">
        <!-- Title Section -->
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h5 class="card-title">Siddar Summary Report</h5>
          <button class="btn btn-primary" @click="downloadExcel">
            Download Excel
          </button>
        </div>

        <!-- Monthly Summary Section -->
        <div class="card mb-4">
          <div class="card-body">
            <h6 class="card-subtitle mb-3">Current Month Summary</h6>
            <div class="row">
              <div class="col-12">
                <div class="alert alert-info d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{{ getCurrentMonthName }} {{ selectedYear }}</strong>
                    <div class="mt-1">Total Siddar Count</div>
                  </div>
                  <div class="h3 mb-0">{{ currentMonthTotal }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Filters Section -->
        <div class="row mb-4">
          <div class="col-md-6 col-lg-4 mb-2">
            <label class="form-label">Month</label>
            <select v-model="selectedMonth" class="form-select">
              <option value="">All Months</option>
              <option v-for="month in months" :key="month.value" :value="month.value">
                {{ month.label }}
              </option>
            </select>
          </div>
          <div class="col-md-6 col-lg-4 mb-2">
            <label class="form-label">Year</label>
            <select v-model="selectedYear" class="form-select">
              <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
            </select>
          </div>
          <div class="col-md-6 col-lg-4 mb-2">
            <label class="form-label">Site Name</label>
            <input 
              type="text" 
              class="form-control" 
              v-model="siteSearch" 
              placeholder="Search by site name"
            >
          </div>
        </div>

        <!-- Clear Filters -->
        <div class="row mb-4">
          <div class="col">
            <button class="btn btn-secondary" @click="clearFilters">
              Clear Filters
            </button>
          </div>
        </div>

        <!-- Table Section -->
        <div class="table-responsive">
          <table class="table table-bordered table-striped">
            <thead class="bg-light">
              <tr>
                <th class="text-nowrap">Month</th>
                <th class="text-nowrap">Site Name</th>
                <th class="text-nowrap">Department</th>
                <th class="text-nowrap">No of Siddar</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in filteredData" :key="index">
                <td class="text-nowrap">{{ item.monthName }}</td>
                <td class="text-nowrap">{{ item.siteName }}</td>
                <td class="text-nowrap">{{ item.department }}</td>
                <td class="text-nowrap">{{ item.numberOfSiddar }}</td>
              </tr>
              <tr v-if="!filteredData.length">
                <td colspan="4" class="text-center">No data available</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { useToast } from 'vue-toast-notification'
import { jsonToExcel } from "@/util/makeExcel"

const $toast = useToast()

interface SiddarSummary {
  month: number
  year: number
  siteName: string
  department: string
  numberOfSiddar: number
  monthName: string
}

const summaryData = ref<SiddarSummary[]>([])
const selectedMonth = ref<number | ''>('')
const selectedYear = ref<number>(new Date().getFullYear())
const siteSearch = ref('')

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
]

const currentYear = new Date().getFullYear()
const years = Array.from({ length: 5 }, (_, i) => currentYear + i)
const baseUrl = process.env.VUE_APP_BASE_URL

const filteredData = computed(() => {
  return summaryData.value.filter((item) => {
    // Month filter
    const monthMatch = !selectedMonth.value || item.month === selectedMonth.value

    // Year filter
    const yearMatch = item.year === selectedYear.value

    // Site name filter
    const siteMatch = !siteSearch.value || 
      item.siteName.toLowerCase().includes(siteSearch.value.toLowerCase())

    return monthMatch && yearMatch && siteMatch
  })
})

const getCurrentMonthName = computed(() => {
  const currentMonth = new Date().getMonth() + 1
  return months.find(m => m.value === currentMonth)?.label || ''
})

const currentMonthTotal = computed(() => {
  const currentMonth = new Date().getMonth() + 1
  return filteredData.value
    .filter(item => item.month === currentMonth)
    .reduce((total, item) => total + item.numberOfSiddar, 0)
})

const fetchSiddarSummary = async () => {
  try {
    const response = await axios.get(`${baseUrl}api/siddar/summary`)
    summaryData.value = response.data
    if (summaryData.value.length === 0) {
      $toast.info('No Siddar summary data found')
    }
  } catch (error) {
    console.error('Error fetching Siddar summary:', error)
    $toast.error('Failed to fetch Siddar summary data')
  }
}

const clearFilters = () => {
  selectedMonth.value = ''
  selectedYear.value = new Date().getFullYear()
  siteSearch.value = ''
}

const downloadExcel = () => {
  const dataToExport = filteredData.value.map((item, index) => ({
    'SL': index + 1,
    'Month': item.monthName,
    'Site Name': item.siteName,
    'Department': item.department,
    'No of Siddar': item.numberOfSiddar
  }))

  const timestamp = new Date().toISOString().split('T')[0]
  jsonToExcel(dataToExport, `siddar-summary-report-${timestamp}.xlsx`)
  $toast.success('Excel file downloaded successfully')
}

onMounted(() => {
  fetchSiddarSummary()
})
</script>

<style scoped>
.table th {
  background-color: #f8f9fa;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.table td {
  vertical-align: middle;
}

.card-subtitle {
  color: #6c757d;
  font-weight: 600;
}

.alert-info {
  background-color: #e8f4f8;
  border-color: #d6e9f0;
  color: #0c5460;
}

.alert-info strong {
  font-size: 1.1rem;
}

.table-sm td, .table-sm th {
  padding: 0.5rem;
}

@media (max-width: 768px) {
  .table-responsive {
    margin: 0 -1rem;
  }
  
  .table {
    margin-bottom: 0;
  }

  .card-body {
    padding: 1rem;
  }
}
</style>
